import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Megaphone,
  ListChecks,
  AlertTriangle,
  CheckCircle2,
  ClipboardList,
  FolderKanban,
} from 'lucide-react';
import { useUser } from '@/shared/hooks/useAuth';
import { ROUTES } from '@/routes/paths';
import { cn } from '@/shared/utils/utils';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { Card } from '@/shared/components/ui/Card';
import { DashboardHero } from '../infrastructure/widgets/DashboardHero';
import { DonutChart } from '../infrastructure/widgets/DonutChart';
import { Briefing, BriefingLine } from '../infrastructure/widgets/Briefing';
import { useMarketingProjects } from '../widgets/marketing/hooks/useMarketingProjects';
import { useMarketingTasks, TaskItem } from '../widgets/marketing/hooks/useMarketingTasks';

const STATUS_DOT: Record<string, string> = {
  todo: 'bg-on-surface-subtle',
  in_progress: 'bg-info',
  review: 'bg-warning',
  done: 'bg-success',
  completed: 'bg-success',
};

const PRIORITY_BADGE: Record<string, string> = {
  critical: 'bg-danger/10 text-danger-text',
  high: 'bg-warning/10 text-warning-text',
  medium: 'bg-primary/10 text-primary',
  low: 'bg-surface-variant text-on-surface-variant',
};

function taskAssignee(t: TaskItem): string {
  return t.assignee_name || t.assigned_name || '';
}

function fmtDate(d?: string) {
  if (!d) return '';
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function SectionHeader({
  icon: Icon,
  title,
  subtitle,
  actionTo,
  actionLabel,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  subtitle: string;
  actionTo?: string;
  actionLabel?: string;
}) {
  return (
    <div className="mb-3 flex items-center justify-between gap-3 border-b border-border pb-3">
      <div className="flex min-w-0 items-center gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-surface-container text-on-surface-variant">
          <Icon className="h-[18px] w-[18px]" />
        </div>
        <div className="min-w-0">
          <h3 className="text-body-sm font-semibold text-on-surface">{title}</h3>
          <p className="truncate text-body-sm text-on-surface-variant">{subtitle}</p>
        </div>
      </div>
      {actionTo && (
        <Link to={actionTo} className="shrink-0 text-body-sm font-semibold text-primary hover:underline">
          {actionLabel || 'View all →'}
        </Link>
      )}
    </div>
  );
}

export function MarketingAdminDashboard() {
  const user = useUser();
  const { data: projects, isLoading: projectsLoading } = useMarketingProjects();
  const { data: tasks, isLoading: tasksLoading } = useMarketingTasks();

  const isLoading = projectsLoading || tasksLoading;

  const stats = useMemo(() => {
    const now = Date.now();
    const allProjects = projects ?? [];
    const allTasks = tasks ?? [];

    const totalProjects = allProjects.length;
    const activeProjects = allProjects.filter((p) => p.status === 'active').length;
    const completedProjects = allProjects.filter((p) => p.status === 'completed').length;
    const onHoldProjects = allProjects.filter((p) => p.status === 'on_hold').length;
    const cancelledProjects = allProjects.filter((p) => p.status === 'cancelled').length;

    const totalTasks = allTasks.length;
    const openTasks = allTasks.filter((t) => t.status !== 'done' && t.status !== 'completed').length;
    const inProgressTasks = allTasks.filter((t) => t.status === 'in_progress').length;
    const overdueTasks = allTasks.filter(
      (t) => t.due_date && new Date(t.due_date).getTime() < now && t.status !== 'done' && t.status !== 'completed',
    ).length;
    const dueTodayTasks = allTasks.filter((t) => {
      if (!t.due_date || t.status === 'done' || t.status === 'completed') return false;
      const d = new Date(t.due_date);
      return d.toDateString() === new Date(now).toDateString();
    }).length;
    const criticalTasks = allTasks.filter(
      (t) => t.priority === 'critical' && t.status !== 'done' && t.status !== 'completed',
    ).length;

    const activeTasksSorted = allTasks
      .filter((t) => t.status !== 'done' && t.status !== 'completed')
      .sort((a, b) => {
        const order: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 };
        const pa = order[a.priority] ?? 4;
        const pb = order[b.priority] ?? 4;
        if (pa !== pb) return pa - pb;
        if (a.due_date && b.due_date) return a.due_date.localeCompare(b.due_date);
        if (a.due_date) return -1;
        if (b.due_date) return 1;
        return 0;
      });

    return {
      totalProjects,
      activeProjects,
      completedProjects,
      onHoldProjects,
      cancelledProjects,
      totalTasks,
      openTasks,
      inProgressTasks,
      overdueTasks,
      dueTodayTasks,
      criticalTasks,
      activeTasksSorted,
    };
  }, [projects, tasks]);

  const briefingLines = useMemo(() => {
    const lines: BriefingLine[] = [];
    if (stats.overdueTasks > 0) {
      lines.push({
        tone: 'neg',
        text: `${stats.overdueTasks} marketing task${stats.overdueTasks === 1 ? ' is' : 's are'} overdue and need${stats.overdueTasks === 1 ? 's' : ''} immediate attention.`,
      });
    }
    if (stats.dueTodayTasks > 0) {
      lines.push({ tone: 'warn', text: `${stats.dueTodayTasks} task${stats.dueTodayTasks === 1 ? '' : 's'} due today.` });
    }
    if (stats.criticalTasks > 0) {
      lines.push({
        tone: 'warn',
        text: `${stats.criticalTasks} critical-priority task${stats.criticalTasks === 1 ? '' : 's'} still open.`,
      });
    }
    if (stats.onHoldProjects > 0) {
      lines.push({ tone: 'info', text: `${stats.onHoldProjects} campaign${stats.onHoldProjects === 1 ? ' is' : 's are'} on hold.` });
    }
    lines.push({
      tone: 'info',
      text: `${stats.activeProjects} of ${stats.totalProjects} marketing campaign${stats.totalProjects === 1 ? '' : 's'} currently active.`,
    });
    if (stats.overdueTasks === 0 && stats.criticalTasks === 0) {
      lines.push({ tone: 'pos', text: 'No overdue or critical-priority items — marketing operations are on track.' });
    }
    return lines;
  }, [stats]);

  const donutSegments = useMemo(
    () =>
      [
        { label: 'Active', value: stats.activeProjects, color: '#12b76a' },
        { label: 'On Hold', value: stats.onHoldProjects, color: '#f79009' },
        { label: 'Completed', value: stats.completedProjects, color: '#0ba5ec' },
        { label: 'Cancelled', value: stats.cancelledProjects, color: '#f04438' },
      ].filter((s) => s.value > 0),
    [stats],
  );

  if (isLoading) {
    return (
      <div className="flex flex-col gap-6">
        <Skeleton className="h-[180px] rounded-[22px]" />
        <Skeleton className="h-[400px] rounded-card" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <DashboardHero
        badge="Digital Marketing"
        badgeIcon={Megaphone}
        title={`Welcome, ${user?.displayName || 'Team'}`}
        subtitle="Track campaigns, marketing tasks, and delivery status across the team."
        heroStats={[
          // One row of figures — a card row underneath used to repeat them.
          { label: 'Active campaigns', value: String(stats.activeProjects), sub: `${stats.totalProjects} total`, icon: Megaphone, tone: 'primary' },
          { label: 'Tasks in progress', value: String(stats.inProgressTasks), sub: `${stats.openTasks} open · ${stats.totalTasks} total`, icon: ListChecks, tone: 'info' },
          { label: 'Overdue tasks', value: String(stats.overdueTasks), sub: stats.overdueTasks > 0 ? 'Needs attention' : 'All on track', icon: AlertTriangle, tone: stats.overdueTasks > 0 ? 'danger' : 'success' },
          { label: 'Completed projects', value: String(stats.completedProjects), sub: `of ${stats.totalProjects} total`, icon: CheckCircle2, tone: 'success' },
        ]}
      />

      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12 lg:col-span-7 rounded-card border border-border bg-card p-4 shadow-xs">
          <SectionHeader
            icon={ClipboardList}
            title="Marketing Tasks"
            subtitle="Active tasks across the team"
            actionTo={ROUTES.tasks}
          />
          {stats.activeTasksSorted.length === 0 ? (
            <div className="py-10 text-center text-body-sm text-on-surface-variant">No active marketing tasks right now.</div>
          ) : (
            <div className="flex flex-col gap-2">
              {stats.activeTasksSorted.slice(0, 6).map((t) => {
                const assignee = taskAssignee(t);
                return (
                  <div
                    key={t._id}
                    className="flex items-center justify-between gap-3 rounded-card-sm border border-border bg-surface-variant px-3.5 py-2.5"
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <span className={cn('h-2 w-2 shrink-0 rounded-full', STATUS_DOT[t.status] || 'bg-on-surface-subtle')} />
                      <div className="min-w-0">
                        <p className="truncate text-data-md font-semibold text-on-surface">{t.title}</p>
                        <p className="truncate text-body-sm text-on-surface-subtle">
                          {t.project_name || 'General Task'}
                          {assignee && ` · ${assignee}`}
                          {t.due_date && ` · Due ${fmtDate(t.due_date)}`}
                        </p>
                      </div>
                    </div>
                    <span
                      className={cn(
                        'shrink-0 rounded-full px-2 py-0.5 text-data-xs font-semibold capitalize',
                        PRIORITY_BADGE[t.priority] || PRIORITY_BADGE.low,
                      )}
                    >
                      {t.priority}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="col-span-12 lg:col-span-5 rounded-card border border-border bg-card p-4 shadow-xs">
          <SectionHeader
            icon={FolderKanban}
            title="Marketing Projects"
            subtitle="Status distribution across campaigns"
            actionTo={ROUTES.projects}
          />
          {stats.totalProjects === 0 ? (
            <div className="py-10 text-center text-body-sm text-on-surface-variant">No marketing projects yet.</div>
          ) : (
            <DonutChart segments={donutSegments} centerValue={String(stats.totalProjects)} centerLabel="Projects" />
          )}
        </div>
      </div>

      <Card className="p-5">
        <SectionHeader
          icon={AlertTriangle}
          title="Daily Briefing"
          subtitle={`Based on ${stats.totalTasks} tasks and ${stats.totalProjects} projects`}
        />
        <Briefing lines={briefingLines} />
      </Card>
    </div>
  );
}
